import { Quaternion, Raycaster, Vector2, Vector3, } from "three";
import { Camera } from "../scenegraph/internal";
import { Behavior } from "./internal";
export var STATE;
(function (STATE) {
    STATE[STATE["NONE"] = -1] = "NONE";
    STATE[STATE["ROTATE"] = 0] = "ROTATE";
    STATE[STATE["DOLLY"] = 1] = "DOLLY";
    STATE[STATE["PAN"] = 2] = "PAN";
    STATE[STATE["TOUCH_ROTATE"] = 3] = "TOUCH_ROTATE";
    STATE[STATE["TOUCH_DOLLY"] = 4] = "TOUCH_DOLLY";
    STATE[STATE["TOUCH_PAN"] = 5] = "TOUCH_PAN";
})(STATE || (STATE = {}));
export var KEYS;
(function (KEYS) {
    KEYS[KEYS["LEFT"] = 37] = "LEFT";
    KEYS[KEYS["UP"] = 38] = "UP";
    KEYS[KEYS["RIGHT"] = 39] = "RIGHT";
    KEYS[KEYS["BOTTOM"] = 40] = "BOTTOM";
})(KEYS || (KEYS = {}));
/**
* Orbit camera controls
*
* @author Robert Wüest
* @author qiao / https://github.com/qiao
* @author mrdoob / http://mrdoob.com
* @author alteredq / http://alteredqualia.com/
* @author WestLangley / http://github.com/WestLangley
* @author erich666 / http://erichaines.com

* @class OrbitCameraBehavior
* @param {SceneGraphNode} parentObject
* @constructor
*/
export class OrbitCameraBehavior extends Behavior {
    /**
     * @method constructor
     * @param {SceneGraphNode} parentObject
     * @param {number[]} position
     * @param {number[]} orientation
     */
    constructor(parentObject) {
        super(parentObject);
        this.enabled = true;
        // "target" sets the location of focus, where the control orbits around
        // and where it pans with respect to.
        this.target = new Vector3();
        // This option actually enables dollying in and out; left as "zoom" for
        // backwards compatibility
        this.noZoom = false;
        this.zoomSpeed = 1.0;
        // Limits to how far you can dolly in and out
        this.minDistance = 0;
        this.maxDistance = Infinity;
        // Set to true to disable this control
        this.noRotate = false;
        this.rotateSpeed = 1.0;
        // Set to true to disable this control
        this.noPan = false;
        this.keyPanSpeed = 7.0; // pixels moved per arrow key push
        // Set to true to automatically rotate around the target
        this.autoRotate = false;
        this.autoRotateSpeed = 2.0; // 30 seconds per round when fps is 60
        // How far you can orbit vertically, upper and lower limits.
        // Range is 0 to Math.PI radians.
        this.minPolarAngle = 0; // radians
        this.maxPolarAngle = Math.PI; // radians
        // Set to true to disable use of the keys
        this.noKeys = false;
        this._EPS = 0.000001;
        this._rotateStart = new Vector2();
        this._rotateEnd = new Vector2();
        this._rotateDelta = new Vector2();
        this._panStart = new Vector2();
        this._panEnd = new Vector2();
        this._panDelta = new Vector2();
        this._panOffset = new Vector3();
        this._offset = new Vector3();
        this._dollyStart = new Vector2();
        this._dollyEnd = new Vector2();
        this._dollyDelta = new Vector2();
        this._phiDelta = 0;
        this._thetaDelta = 0;
        this._scale = 1;
        this._pan = new Vector3();
        this._lastPosition = new Vector3();
        this._lastQuaternion = new Quaternion();
        this._state = STATE.NONE;
        // events
        this._changeEvent = { type: "change" };
        this._startEvent = { type: "start" };
        this._endEvent = { type: "end" };
        this._cameraObj = this.parentObject.iCamera;
        this._target0 = this.target.clone();
        this._position0 = this._cameraObj.position.clone();
        this._cameraObj.up.set(0, 0, 1);
        this._quat = new Quaternion().setFromUnitVectors(this._cameraObj.up, new Vector3(0, 1, 0));
        this._tMouseVector = new Vector3();
        this._quatInverse = this._quat.clone().invert();
        if (!(parentObject instanceof Camera)) {
            throw "Wrong Parent Object: SceneCamera Behavior must be attached to a Camera object.";
        }
    }
    /**
     * Will be called once, when rendering starts
     * @method init
     * @param {TraversalState} traversalState
     */
    init(traversalState) {
        this.traversalState = traversalState;
    }
    /**
     * Will be called in every render pass
     *
     * @method render
     * @param {TraversalState} traversalState
     */
    render(traversalState) {
        this.traversalState = traversalState;
        if (traversalState.mouseDown) {
            if (this.enabled === false)
                return;
            const event = traversalState.mouseDownArguments;
            if (event) {
                event.preventDefault();
                if (event.button === 0) {
                    if (this.noRotate === true)
                        return;
                    this._state = STATE.ROTATE;
                    this._rotateStart.set(event.clientX, event.clientY);
                }
                else if (event.button === 1) {
                    if (this.noZoom === true)
                        return;
                    this._state = STATE.DOLLY;
                    this._dollyStart.set(event.clientX, event.clientY);
                }
                else if (event.button === 2) {
                    if (this.noPan === true)
                        return;
                    this._state = STATE.PAN;
                    this._panStart.set(event.clientX, event.clientY);
                }
            }
        }
        if (traversalState.mouseMove) {
            if (this.enabled === false)
                return;
            const event = traversalState.mouseMoveArguments;
            if (event) {
                event.preventDefault();
                const element = traversalState.scene.engine.renderer.domElement;
                if (this._state === STATE.ROTATE) {
                    if (this.noRotate === true)
                        return;
                    this._rotateEnd.set(event.clientX, event.clientY);
                    this._rotateDelta.subVectors(this._rotateEnd, this._rotateStart);
                    // rotating across whole screen goes 360 degrees around
                    this.rotateLeft(((2 * Math.PI * this._rotateDelta.x) / element.clientWidth) * this.rotateSpeed);
                    // rotating up and down along whole screen attempts to go 360, but limited to 180
                    this.rotateUp(((2 * Math.PI * this._rotateDelta.y) / element.clientHeight) * this.rotateSpeed);
                    this._rotateStart.copy(this._rotateEnd);
                }
                else if (this._state === STATE.DOLLY) {
                    if (this.noZoom === true)
                        return;
                    this._dollyEnd.set(event.clientX, event.clientY);
                    this._dollyDelta.subVectors(this._dollyEnd, this._dollyStart);
                    if (this._dollyDelta.y > 0) {
                        this.dollyIn();
                    }
                    else {
                        this.dollyOut();
                    }
                    this._dollyStart.copy(this._dollyEnd);
                }
                else if (this._state === STATE.PAN) {
                    if (this.noPan === true)
                        return;
                    this._panEnd.set(event.clientX, event.clientY);
                    this._panDelta.subVectors(this._panEnd, this._panStart);
                    this.pan(this._panDelta.x, this._panDelta.y);
                    this._panStart.copy(this._panEnd);
                }
                this.update();
            }
        }
        if (traversalState.mouseUp) {
            if (this.enabled === false)
                return;
            this._state = STATE.NONE;
        }
        if (traversalState.mouseWheel) {
            if (this.enabled === false || this.noZoom === true)
                return;
            const evt = traversalState.mouseWheelArguments;
            if (evt) {
                evt.preventDefault();
                evt.stopPropagation();
                let delta = 0;
                if (evt.wheelDelta !== undefined) {
                    // WebKit / Opera / Explorer 9
                    delta = evt.wheelDelta;
                }
                else if (evt.detail !== undefined) {
                    // Firefox
                    delta = -evt.detail;
                }
                if (delta > 0) {
                    this.dollyOut();
                }
                else {
                    this.dollyIn();
                }
                this.update();
            }
        }
        if (traversalState.keyDown) {
            if (this.enabled === false || this.noKeys === true || this.noPan === true)
                return;
            switch (traversalState.key) {
                case KEYS.UP:
                    this.pan(0, this.keyPanSpeed);
                    this.update();
                    break;
                case KEYS.BOTTOM:
                    this.pan(0, -this.keyPanSpeed);
                    this.update();
                    break;
                case KEYS.LEFT:
                    this.pan(this.keyPanSpeed, 0);
                    this.update();
                    break;
                case KEYS.RIGHT:
                    this.pan(-this.keyPanSpeed, 0);
                    this.update();
                    break;
            }
        }
        //update the raycaster for picking
        this._tMouseVector.x =
            2 * (traversalState.actualMousePos[0] / traversalState.scene.engine.getContextWidth()) - 1;
        this._tMouseVector.y =
            1 - 2 * (traversalState.actualMousePos[1] / traversalState.scene.engine.getContextHeight());
        // set two vectors with opposing z values
        this._tMouseVector.z = -1.0;
        let vector = this._tMouseVector.clone();
        const camera = traversalState.cameras["sceneCamera"].iCamera;
        let end = new Vector3(vector.x, vector.y, 1.0);
        vector = vector.unproject(camera);
        end = end.unproject(camera);
        // find direction from vector to end
        end.sub(vector).normalize();
        traversalState.rayCaster = new Raycaster(vector, end); //this._tProjector.pickingRay( this._tMouseVector.clone(), traversalState.cameras["sceneCamera"].iCamera );
    }
    rotateLeft(angle) {
        if (angle === undefined)
            angle = this.getAutoRotationAngle();
        this._thetaDelta -= angle;
    }
    rotateUp(angle) {
        if (angle === undefined)
            angle = this.getAutoRotationAngle();
        this._phiDelta -= angle;
    }
    // pass in distance in world space to move left
    panLeft(distance) {
        const te = this._cameraObj.matrix.elements;
        // get X column of matrix
        this._panOffset.set(te[0], te[1], te[2]);
        this._panOffset.multiplyScalar(-distance);
        this._pan.add(this._panOffset);
    }
    // pass in distance in world space to move up
    panUp(distance) {
        const te = this._cameraObj.matrix.elements;
        // get Y column of matrix
        this._panOffset.set(te[4], te[5], te[6]);
        this._panOffset.multiplyScalar(distance);
        this._pan.add(this._panOffset);
    }
    // pass in x,y of change desired in pixel space,
    // right and down are positive
    pan(deltaX, deltaY) {
        const element = this.traversalState.scene.engine.renderer.domElement;
        if (this._cameraObj.fov !== undefined) {
            // perspective
            const position = this._cameraObj.position;
            const offset = position.clone().sub(this.target);
            let targetDistance = offset.length();
            // half of the fov is center to top of screen
            targetDistance *= Math.tan(((this._cameraObj.fov / 2) * Math.PI) / 180.0);
            // we actually don't use screenWidth, since perspective camera is fixed to screen height
            this.panLeft((2 * deltaX * targetDistance) / element.clientHeight);
            this.panUp((2 * deltaY * targetDistance) / element.clientHeight);
        }
        else if (this._cameraObj.top !== undefined) {
            // orthographic
            this.panLeft((deltaX * (this._cameraObj.right - this._cameraObj.left)) /
                element.clientWidth);
            this.panUp((deltaY * (this._cameraObj.top - this._cameraObj.bottom)) /
                element.clientHeight);
        }
        else {
            // camera neither orthographic or perspective
            console.warn("WARNING: OrbitControls.js encountered an unknown camera type - pan disabled.");
        }
    }
    dollyIn(dollyScale) {
        if (dollyScale === undefined)
            dollyScale = this.getZoomScale();
        this._scale /= dollyScale;
    }
    dollyOut(dollyScale) {
        if (dollyScale === undefined)
            dollyScale = this.getZoomScale();
        this._scale *= dollyScale;
    }
    update() {
        const position = this._cameraObj.position;
        this._offset.copy(position).sub(this.target);
        // rotate offset to "y-axis-is-up" space
        this._offset.applyQuaternion(this._quat);
        // angle from z-axis around y-axis
        let theta = Math.atan2(this._offset.x, this._offset.z);
        // angle from y-axis
        let phi = Math.atan2(Math.sqrt(this._offset.x * this._offset.x + this._offset.z * this._offset.z), this._offset.y);
        if (this.autoRotate) {
            this.rotateLeft(this.getAutoRotationAngle());
        }
        theta += this._thetaDelta;
        phi += this._phiDelta;
        // restrict phi to be between desired limits
        phi = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, phi));
        // restrict phi to be betwee EPS and PI-EPS
        phi = Math.max(this._EPS, Math.min(Math.PI - this._EPS, phi));
        let radius = this._offset.length() * this._scale;
        // restrict radius to be between desired limits
        radius = Math.max(this.minDistance, Math.min(this.maxDistance, radius));
        // move target to panned location
        this.target.add(this._pan);
        this._offset.x = radius * Math.sin(phi) * Math.sin(theta);
        this._offset.y = radius * Math.cos(phi);
        this._offset.z = radius * Math.sin(phi) * Math.cos(theta);
        // rotate offset back to "camera-up-vector-is-up" space
        this._offset.applyQuaternion(this._quatInverse);
        position.copy(this.target).add(this._offset);
        this._cameraObj.lookAt(this.target);
        this._thetaDelta = 0;
        this._phiDelta = 0;
        this._scale = 1;
        this._pan.set(0, 0, 0);
        // update condition is:
        // min(camera displacement, camera rotation in radians)^2 > EPS
        // using small-angle approximation cos(x/2) = 1 - x^2 / 8
        if (this._lastPosition.distanceToSquared(this._cameraObj.position) > this._EPS ||
            8 *
                (1 -
                    this._lastQuaternion.dot(new Quaternion(this._cameraObj.quaternion.x, this._cameraObj.quaternion.y, this._cameraObj.quaternion.z, 1))) >
                this._EPS) {
            this._lastPosition.copy(this._cameraObj.position);
            this._lastQuaternion.copy(this._cameraObj.quaternion);
        }
    }
    reset() {
        this._state = STATE.NONE;
        this.target.copy(this._target0);
        this._cameraObj.position.copy(this._position0);
        this.update();
    }
    getAutoRotationAngle() {
        return ((2 * Math.PI) / 60 / 60) * this.autoRotateSpeed;
    }
    getZoomScale() {
        return Math.pow(0.95, this.zoomSpeed);
    }
}
//# sourceMappingURL=orbitcamerabehavior.js.map